import cx from 'classnames';
import Loader from 'components/loaders';
import { MediaController, MediaPlayButton } from 'media-chrome/dist/react';
import { AnimatePresence, motion, useScroll, useTransform } from 'motion/react';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState } from 'react';
import { isAndroid, isIOS } from 'react-device-detect';
import shallow from 'zustand/shallow';

import Cover from 'components/cover';
import PlayButton from 'components/play-button';
import VideoPlayer from 'components/video-player-dubbing';
import * as styles from 'components/video-player-dubbing/InternalPlayer/Player.styles';
import useVideoPlayerStore from 'components/video-player-dubbing/store';

import useDubbingSurvey from 'components/video-player-dubbing/InternalPlayer/hooks/useDubbingSurvey';
import getImageFromSet from 'lib/get-image-from-set';
import { useIsBreakpointWidth } from 'lib/hooks/useIsBreakpointWidth';
import { VideoData } from 'lib/pages/talks/slug/[...slug].props';

const EndCard = dynamic(() => import('./EndCard'), {
  ssr: false
});

const SubtitleCredits = dynamic(
  () =>
    import(
      'components/video-player-dubbing/InternalPlayer/Subtitles/subtitle-credits'
    ),
  { ssr: false }
);

type HeroProps = {
  video: VideoData;
};

const MIN_VIDEO_WIDTH = 640;

const Hero = ({
  ref,
  video
}: HeroProps & {
  ref: React.RefObject<HTMLDivElement | null>;
}): React.ReactNode => {
  const [hovered, setHovered] = useState<boolean>(false);

  const { surveyDismissed } = useDubbingSurvey();
  const { scrollYProgress } = useScroll();
  const scrollPadding = useTransform(scrollYProgress, [0, 0.1], [20, 0]);

  useEffect(() => {
    if (surveyDismissed) {
      setHovered(false);
    }
  }, [surveyDismissed]);

  const image = useMemo(
    () => getImageFromSet(video?.primaryImageSet, '16x9'),
    [video]
  );

  const removeMinWidth = useIsBreakpointWidth({
    size: 'lg'
  });

  const isMobileDevice = useMemo(() => isAndroid || isIOS, []);
  const isMobileWeb = useIsBreakpointWidth({
    size: 'sm',
    breakPointType: 'tui'
  });
  const isMobile = isMobileDevice || isMobileWeb;

  const { playbackMode, ready, requestPlay, loadingDubLang, roundedTime } =
    useVideoPlayerStore(
      state => ({
        ...state
      }),
      shallow
    );

  const videoWrapperClasses = cx(
    ready ? 'pointer-events-auto' : 'pointer-events-none'
  );

  const videoLoaderClasses = cx(
    'pointer-events-none absolute z-10 flex size-full items-center justify-center'
  );

  return (
    <AnimatePresence>
      <motion.div
        className="aspect-h-9 sticky top-0 z-20 bg-white sm:-mx-5 lg:top-[unset] lg:block"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        {...(removeMinWidth && {
          style: {
            paddingLeft: scrollPadding,
            paddingRight: scrollPadding
          }
        })}
      >
        <div ref={ref} className="relative">
          <div className="md:hidden">
            <SubtitleCredits />
          </div>

          <MediaController
            autohide={isMobile ? '2' : '-1'}
            noAutohide
            defaultStreamType="ondemand"
            style={{
              ...styles.mediaControllerStyles,
              minWidth: removeMinWidth ? 0 : MIN_VIDEO_WIDTH,
              display: 'flex'
            }}
            className="size-full"
            suppressHydrationWarning
          >
            {playbackMode === 'ended' && (
              <>
                <div className="bg-opacity-75 absolute bottom-0 left-0 right-0 top-0 z-10 size-full bg-black text-white">
                  <EndCard />
                </div>
              </>
            )}

            {loadingDubLang ? (
              <div className={videoLoaderClasses}>
                <Loader.Spinner size="22" color="white" />
              </div>
            ) : (
              <div
                slot="centered-chrome"
                id="media-wrapper"
                className={videoWrapperClasses}
                style={styles.centeredChromeStyles}
              >
                <button
                  id="play-button"
                  type="button"
                  onClick={requestPlay}
                  className="media-play-button"
                >
                  <MediaPlayButton
                    aria-label="play"
                    tabIndex={0}
                    role="button"
                    suppressHydrationWarning
                  >
                    <span slot="icon">
                      <PlayButton />
                    </span>
                  </MediaPlayButton>
                </button>
              </div>
            )}
            {roundedTime === 0 && (
              <div slot="poster">
                <Cover imageUrl={image ?? ''} />
              </div>
            )}

            <VideoPlayer
              key={video.id}
              video={video}
              hovered={!surveyDismissed || hovered}
            />
          </MediaController>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default Hero;
